'use client';

import type { SupportedLanguages } from '@pierre/diffs';
import { WorkerPoolContext } from '@pierre/diffs/react';
import type { WorkerPoolManager, WorkerStats } from '@pierre/diffs/worker';
import { getOrCreateWorkerPoolSingleton } from '@pierre/diffs/worker';
import type { ReactNode } from 'react';
import { createContext, use, useLayoutEffect, useState } from 'react';

import { usePrV2ViewPreferencesStore } from '@/lib/stores/pr-v2-view-preferences-store';

const workerFactory = () =>
  new Worker(new URL('@pierre/diffs/worker/worker.js', import.meta.url), {
    type: 'module',
  });

function detectWorkerSupport() {
  if (typeof Worker === 'undefined') {
    return false;
  }

  try {
    const worker = workerFactory();
    worker.terminate();
    return true;
  } catch {
    return false;
  }
}

const WARM_UP_LANGUAGES: SupportedLanguages[] = [
  'typescript',
  'tsx',
  'javascript',
  'python',
  'go',
  'json',
  'yaml',
];

let canUseWorkers: boolean | undefined;

/**
 * The tab's diff worker pool, created by the first call and never terminated, so its workers
 * and render caches outlive any one PR. `undefined` where workers can't run.
 */
function getPrV2DiffWorkerPool({ preloadLanguages = false } = {}): WorkerPoolManager | undefined {
  canUseWorkers ??= detectWorkerSupport();
  if (!canUseWorkers) {
    return undefined;
  }

  const { highlightTheme, lineDiffType } = usePrV2ViewPreferencesStore.getState();
  return getOrCreateWorkerPoolSingleton({
    poolOptions: { workerFactory, poolSize: 2 },
    highlighterOptions: {
      theme: highlightTheme,
      lineDiffType,
      langs: preloadLanguages ? WARM_UP_LANGUAGES : undefined,
      // Shiki's Oniguruma engine highlighted a 5,750-line diff in half the time of its default
      // JavaScript regex engine.
      preferredHighlighter: 'shiki-wasm',
    },
  });
}

/**
 * Starts the diff worker pool ahead of the first PR, preloading common grammars. A cold load
 * straight to a PR creates the pool without them so its first diff waits for nothing extra.
 */
export function warmPrV2DiffWorkerPool() {
  getPrV2DiffWorkerPool({ preloadLanguages: true });
}

// Pierre draws no rows until its worker pool initializes (seconds on the first PR after a page
// load), so files would paint as bare headers and then shove the list down as each body lands.
// Without a pool Pierre renders on the main thread, so it can always draw.
const DiffRendererReadyContext = createContext(true);

/** Whether Pierre can draw diff rows yet; until then the diff shows its skeleton. */
export function usePrV2DiffRendererReady() {
  return use(DiffRendererReadyContext);
}

// A pool whose workers failed leaves Pierre rendering on the main thread.
function canDrawRows({ managerState, workersFailed }: WorkerStats) {
  return managerState === 'initialized' || workersFailed;
}

/**
 * Provides the tab's Pierre worker pool to the PR diff. Mounted above the diff's loading state,
 * so the pool initializes while code changes download. Unmounting leaves the pool running for
 * the next PR. Once the pool runs, every diff under it takes its theme and `lineDiffType` from the
 * pool (the viewer's diff preferences) and ignores its own options for those.
 */
export function PrV2DiffWorkerPool({ children }: { children: ReactNode }) {
  const [workerPool] = useState(() => getPrV2DiffWorkerPool());

  if (!workerPool) {
    return children;
  }

  return (
    <WorkerPoolContext value={workerPool}>
      <PrV2DiffWorkerPoolReadiness workerPool={workerPool}>{children}</PrV2DiffWorkerPoolReadiness>
    </WorkerPoolContext>
  );
}

function PrV2DiffWorkerPoolReadiness({
  workerPool,
  children,
}: {
  workerPool: WorkerPoolManager;
  children: ReactNode;
}) {
  const lineDiffType = usePrV2ViewPreferencesStore((state) => state.lineDiffType);
  const highlightTheme = usePrV2ViewPreferencesStore((state) => state.highlightTheme);
  // Latched: once the diff has painted it never drops back to the skeleton.
  const [isReady, setIsReady] = useState(() => canDrawRows(workerPool.getStats()));

  // Layout effect: sync worker Shiki options as early as possible so the shared pool matches the
  // diff's `diffOptions` before paint.
  useLayoutEffect(() => {
    void workerPool.setRenderOptions({ theme: highlightTheme, lineDiffType });
  }, [highlightTheme, lineDiffType, workerPool]);

  useLayoutEffect(() => {
    if (isReady) {
      return;
    }

    return workerPool.subscribeToStatChanges((stats) => {
      if (canDrawRows(stats)) {
        setIsReady(true);
      }
    });
  }, [isReady, workerPool]);

  return <DiffRendererReadyContext value={isReady}>{children}</DiffRendererReadyContext>;
}
